import * as d3 from 'd3';
import * as jStat from 'jstat';
import _ from 'lodash';
import * as setup from 'setup';
import { deepClone } from '../common/common';
import { roundNear, transformValue } from '../numbers/numbers';

export function getAccuracy(target: number, value: number) {
    return Math.round(Math.abs((value - target) / target) * 100);
}

export function getStandardDeviation(values: number[]) {
    const avg = _.mean(values);
    const diffs = values.map(value => value - avg);
    const squareDiffs = diffs.map(diff => diff * diff);
    const avgSquareDiff = _.mean(squareDiffs);
    return Math.sqrt(avgSquareDiff);
}

export function getHistogramBins(values: number[]) {
    const histogram = d3.bin()
        .thresholds(6);

    return histogram(values)
        .filter(bin => bin.length > 0) as HistogramBin[];
}

export function getNormalDistributionValue(value: number, mean: number, standardDeviation: number) {
    return jStat.normal.pdf(value, mean, standardDeviation) as number;
}

export function getNormalDistribution(values: number[]) {
    const min = _.min(values) || 0;
    const max = _.max(values) || 0;
    const mean = values.length ? _.mean(values) : 0;
    const percentile = 0.9;
    const standardDeviation = (max - min) / (jStat.normal.inv(percentile + (1 - percentile) / 2, 0, 1) * 2);

    // TODO: Why are lower 5% and upper 95% while standardDeviation uses 90%?
    return {
        lower: getNormalInverse(mean, standardDeviation, 0.05),
        max,
        mean,
        min,
        standardDeviation,
        upper: getNormalInverse(mean, standardDeviation, 0.95),
    } as NormalDistribution;
}

export function compareNormalDistributions(values1: number[], values2: number[], threshold = 0.05) {
    const d1 = getNormalDistribution(values1);
    const d2 = getNormalDistribution(values2);
    const mean = checkErrorMargin(d1.mean, d2.mean, threshold);
    const standardDeviation = checkErrorMargin(d1.standardDeviation, d2.standardDeviation, threshold);
    return mean && standardDeviation;
}

export function getNormalInverse(mean: number, standardDeviation: number, percentile?: number) {
    const p = percentile !== undefined ? percentile : Math.random();
    return jStat.normal.inv(p, mean, standardDeviation) as number;
}

export function getPercentile(values: number[], percentile: number) {
    return jStat.percentile(values, percentile) as number;
}

export function checkErrorMargin(a: number, b: number, threshold = 0.05) {
    return Math.abs((a - b) / b) <= threshold;
}

export function compareConversions(a: number, b: number, multiplier: number, decimals: number) {
    const aValue = transformValue(a, multiplier, decimals);
    const bValue = transformValue(b, multiplier, decimals);
    return checkErrorMargin(aValue, bValue);
}

export function getResults(results: GameResults, prediction: number, week: number) {
    let newResults = deepClone(results);
    switch (week) {
        case 4: newResults = getResultsWeek4(newResults, prediction); break;
        case 5: newResults = getResultsWeek5(newResults); break;
        case 6: newResults = getResultsWeek6(newResults); break;
        case 8: newResults = getResultsWeek8(newResults); break;
        case 28: newResults = getResultsWeek28(newResults); break;
    }
    return newResults;
}

export function getResultsWeek4(results: GameResults, prediction: number) {
    const { standardDeviation } = setup.game;

    // Get prediction range
    const step5Deviation = setup.game.steps[4].deviation as NumberRange;
    const base = _.random(step5Deviation.min, step5Deviation.max) * prediction;

    // Get days
    const days = _.range(28).map(index => ({
        step1: 0,
        step2: 0,
        step3: 0,
        step4: 0,
        step4Retention: 0,
        step5: index ? getNormalInverse(base, base * standardDeviation.default) : base,
    }));

    // Return results
    return parseResults({
        ...results,
        days,
    });
}

export function getResultsWeek5(results: GameResults): GameResults {
    const { standardDeviation, steps: [setupStep1, setupStep2, setupStep3, setupStep4] } = setup.game;
    const [base1, base2, base3, base4, base5] = results.bases;

    // Get bounds bottom to top based first result
    base5.min = results.days[0].step5;
    base5.max = base5.max = base5.min;
    base4.min = Math.round(base5.min / setupStep4.conversion.max);
    base4.max = Math.round(base5.max / setupStep4.conversion.min);
    base3.min = Math.round(base4.min / setupStep3.conversion.max);
    base3.max = Math.round(base4.max / setupStep3.conversion.min);
    base2.min = Math.round(base3.min / setupStep2.conversion.max);
    base2.max = Math.round(base3.max / setupStep2.conversion.min);
    base1.min = Math.round(base2.min / setupStep1.conversion.max);
    base1.max = Math.round(base2.max / setupStep1.conversion.min);

    // Update bounds based on constraints and get values
    for (let i = 0; i < results.bases.length; i++) {
        const setupStep = setup.game.steps[i];
        const base = results.bases[i];

        // Update base
        base.min = Math.max(base.min, setupStep.constraint.min);
        base.max = Math.min(base.max, setupStep1.constraint.max);
        base.step = Math.round(_.random(base.min, base.max));

        // Update previous base
        if (i) {
            const prevBase = results.bases[i - 1];
            prevBase.conversion = base.step / prevBase.step;
        }
    }

    // Check conversion bounds
    const valid = results.bases.every((base, index) => {
        const setupStep = setup.game.steps[index];
        return base.conversion >= setupStep.conversion.min && base.conversion <= setupStep.conversion.max;
    });
    if (!valid) {
        return getResultsWeek5(results);
    }

    // Get days
    // TODO: Randomize percentiles to split results into 6 buckets
    const percentiles = [0.30, 0.51, 0.49, 0.50, 0.55, 0.58, 0.37];
    const days = getResultsConversionDays(results, percentiles, standardDeviation.default, 28);

    // Return results
    return parseResults({
        ...results,
        days,
    });
}

export function getResultsWeek6(results: GameResults) {
    const { outlier, standardDeviation } = setup.game;
    const [, base2, base3, base4] = results.bases;

    // TODO: Randomize percentiles to split results into 6 buckets
    const percentiles = [0.20, 0.70, 0.37, 0.50, 0.60, 0.60, outlier.percentile];
    const days = getResultsConversionDays(results, percentiles, standardDeviation.default, 35);

    // Get outlier conversions
    const step2Conversion = getNormalInverse(base2.conversion, base2.conversion * standardDeviation.default, outlier.percentile);
    const step3Conversion = getNormalInverse(base3.conversion, base3.conversion * standardDeviation.default, outlier.percentile);
    const step4Conversion = getNormalInverse(base4.conversion, base4.conversion * standardDeviation.default, outlier.percentile);

    // Update outlier day
    const outlierDay = days[outlier.day];
    outlierDay.step2 = (_.max(days.slice(35).map(result => result.step2)) || 0) * outlier.multiplier;
    outlierDay.step3 = outlierDay.step2 * step2Conversion;
    outlierDay.step4 = outlierDay.step3 * step3Conversion;
    outlierDay.step5 = outlierDay.step4 * step4Conversion;

    // Return results
    return parseResults({
        ...results,
        days,
    });
}

export function getResultsWeek8(results: GameResults) {
    const { standardDeviation } = setup.game;

    // Get days
    const percentiles = _.range(14).map(() => Math.random());
    const days = getResultsConversionDays(results, percentiles, standardDeviation.default, 42);

    // Return results
    return parseResults({
        ...results,
        days,
    });
}

export function getResultsWeek28(results: GameResults) {
    const { retention, standardDeviation } = setup.game;
    const [base1, base2, base3, base4] = results.bases;

    // Get days
    const newDays: GameResultDay[] = [];
    _.range(140).map(index => {
        // Get conversions
        const step1Conversion = getNormalInverse(base1.conversion, base1.conversion * standardDeviation.week28);
        const step2Conversion = getNormalInverse(base2.conversion, base2.conversion * standardDeviation.week28);
        const step3Conversion = getNormalInverse(base3.conversion, base3.conversion * standardDeviation.week28);
        const step4Conversion = getNormalInverse(base4.conversion, base4.conversion * standardDeviation.week28);

        // Get retention
        const step4RetentionDay = newDays[index - retention.days];
        const step4Retention = step4RetentionDay ? getNormalInverse(retention.value, retention.value * standardDeviation.week28) : 0;
        const step4RetentionValue = step4RetentionDay ? step4Retention * step4RetentionDay.step4 : 0;

        // Get steps
        const step1 = getNormalInverse(base1.step, base1.step * standardDeviation.week28);
        const step2 = step1 * step1Conversion;
        const step3 = step2 * step2Conversion + step4RetentionValue;
        const step4 = step3 * step3Conversion;
        const step5 = step4 * step4Conversion;

        // Add day
        const day = {
            step1,
            step2,
            step3,
            step4,
            step4Retention,
            step5,
        };
        newDays.push(day);
    });

    // Merge with previous days
    const days = [
        ...results.days.slice(0, 56),
        ...newDays,
    ];

    // Return results
    return parseResults({
        ...results,
        days,
    });
}

export function getResultsConversionDays(results: GameResults, percentiles: number[], standardDeviation: number, offset: number) {
    const [base1, base2, base3, base4, base5] = results.bases;

    // Get days
    const days = percentiles.map(percentile => {
        // Get conversions
        const step2Conversion = getNormalInverse(base2.conversion, base2.conversion * standardDeviation);
        const step3Conversion = getNormalInverse(base3.conversion, base3.conversion * standardDeviation);
        const step4Conversion = getNormalInverse(base4.conversion, base4.conversion * standardDeviation);

        // Get steps
        const step5 = getNormalInverse(base5.step, base5.step * standardDeviation);
        const step4 = step5 / step4Conversion;
        const step3 = step4 / step3Conversion;
        const step2 = step3 / step2Conversion;
        const step1 = getNormalInverse(base1.step, base1.step * standardDeviation, percentile);

        return {
            step1,
            step2,
            step3,
            step4,
            step4Retention: 0,
            step5,
        };
    });

    // Merge with previous days
    return [
        ...results.days.slice(0, offset),
        ...days,
        ...results.days.slice(offset + days.length),
    ];
}

export function parseResults(results: GameResults) {
    // Parse days
    const days = results.days.map(day => ({
        ...day,
        step1: Math.round(day.step1),
        step2: Math.round(day.step2),
        step3: Math.round(day.step3),
        step4: Math.round(day.step4),
        step5: Math.round(day.step5),
    }));

    return {
        ...results,
        days,
    } as GameResults;
}

export function getWeeklyResults(days: GameResultDay[], offset = 4) {
    const count = Math.max(0, days.length - (offset * 7)) / 7;
    return _.range(count).map(index => {
        const segment = sliceResultsByWeek(days, index + offset + 1);
        return {
            step1: _.sum(segment.map(day => day.step1)),
            step2: _.sum(segment.map(day => day.step2)),
            step3: _.sum(segment.map(day => day.step3)),
            step4: _.sum(segment.map(day => day.step4)),
            step4Retention: _.mean(segment.map(day => day.step4Retention)),
            step5: _.sum(segment.map(day => day.step5)),
        } as GameResultDay;
    });
}

export function sliceResultsByWeek(days: GameResultDay[], start: number, end?: number) {
    const startDay = (start - 1) * 7;
    const endDay = (end || start) * 7;
    return days.slice(startDay, endDay);
}

export function parseStepInitialBounds(steps: GameStep[], prediction: number): GameStep[] {
    const [setupStep1, setupStep2, setupStep3, setupStep4, setupStep5] = setup.game.steps;
    const newSteps = steps.map(step => ({ ...step }));
    const [step1, step2, step3, step4, step5] = newSteps;

    // Get bounds bottom to top based on prediction
    const step5PredictionMin = prediction * (setupStep5.deviation?.min || 0);
    const step5PredictionMax = prediction * (setupStep5.deviation?.max || 0);
    const step4PredictionMin = step5PredictionMin / setupStep4.conversion.initialMax;
    const step4PredictionMax = step5PredictionMax / setupStep4.conversion.initialMin;
    const step3PredictionMin = step4PredictionMin / setupStep3.conversion.initialMax;
    const step3PredictionMax = step4PredictionMax / setupStep3.conversion.initialMin;
    const step2PredictionMin = step3PredictionMin / setupStep2.conversion.initialMax;
    const step2PredictionMax = step3PredictionMax / setupStep2.conversion.initialMin;
    const step1PredictionMin = step2PredictionMin / setupStep1.conversion.initialMax;
    const step1PredictionMax = step2PredictionMax / setupStep1.conversion.initialMin;

    // Get bounds top to bottom based on inputs
    const step2InputMin = (step1.max * setupStep1.conversion.initialMin) || setupStep2.constraint.min;
    const step2InputMax = (step1.max * setupStep1.conversion.initialMax) || setupStep2.constraint.max;
    const step3InputMin = (step2.max * setupStep2.conversion.initialMin) || setupStep3.constraint.min;
    const step3InputMax = (step2.max * setupStep2.conversion.initialMax) || setupStep3.constraint.max;
    const step4InputMin = (step3.max * setupStep3.conversion.initialMin) || setupStep4.constraint.min;
    const step4InputMax = (step3.max * setupStep3.conversion.initialMax) || setupStep4.constraint.max;

    // Set bounds based on constraints, prediction and inputs
    step1.bounds.min = Math.max(setupStep1.constraint.min, step1PredictionMin);
    step1.bounds.max = Math.min(setupStep1.constraint.max, step1PredictionMax);
    step2.bounds.min = Math.max(setupStep2.constraint.min, step2PredictionMin, step2InputMin);
    step2.bounds.max = Math.min(setupStep2.constraint.max, step2PredictionMax, step2InputMax);
    step3.bounds.min = Math.max(setupStep3.constraint.min, step3PredictionMin, step3InputMin);
    step3.bounds.max = Math.min(setupStep3.constraint.max, step3PredictionMax, step3InputMax);
    step4.bounds.min = Math.max(setupStep4.constraint.min, step4PredictionMin, step4InputMin);
    step4.bounds.max = Math.min(setupStep4.constraint.max, step4PredictionMax, step4InputMax);
    step5.bounds.min = Math.max(setupStep5.constraint.min, step5PredictionMin);
    step5.bounds.max = Math.min(setupStep5.constraint.max, step5PredictionMax);

    // Parse values
    newSteps.forEach(step => {
        step.bounds.min = roundNear(step.bounds.min);
        step.bounds.max = roundNear(step.bounds.max);
        step.placeholder = roundNear(_.mean([step.bounds.min, step.bounds.max]));
    });

    return newSteps;
}

export function fillSteps(steps: GameStep[], results: GameResults, prediction: number, type: GameFillStepsType) {
    const newSteps = deepClone(steps);
    switch (type) {
        case 'conversions': return fillStepConversions(newSteps, results);
        case 'initials': return fillStepInitials(newSteps, prediction);
        case 'mins': return fillStepMins(newSteps);
        case 'ranges': return fillStepRanges(newSteps, results);
    }
}

export function fillStepInitials(steps: GameStep[], prediction: number) {
    // Loop through continually updated steps
    for (let i = 0; i < steps.length; i++) {
        // Update steps
        steps = parseStepInitialBounds(steps, prediction);

        // Update step values
        const step = steps[i];
        step.max = step.placeholder;
        step.initial = step.max;

        // Update previous step conversion
        if (i) {
            const prevStep = steps[i - 1];
            prevStep.conversion.max = step.max / prevStep.max;
        }
    }

    return parseStepRanges(steps);
}

export function fillStepMins(steps: GameStep[]) {
    return steps.map(step => ({
        ...step,
        conversion: {
            ...step.conversion,
            min: step.conversion.max,
        },
        min: step.max,
    }));
}

export function fillStepConversions(steps: GameStep[], results: GameResults) {
    const resultWeek5 = sliceResultsByWeek(results.days, 5);

    // Get result means
    const resultMeans = [
        _.mean(resultWeek5.map(day => day.step1)),
        _.mean(resultWeek5.map(day => day.step2)),
        _.mean(resultWeek5.map(day => day.step3)),
        _.mean(resultWeek5.map(day => day.step4)),
        _.mean(resultWeek5.map(day => day.step5)),
    ];

    // Get conversion means
    const conversionMeans = resultMeans.map((resultMean, index) => {
        const nextMean = resultMeans[index + 1] || 0;
        return nextMean / resultMean;
    });

    // Update step conversions
    steps.forEach((step, index) => {
        step.conversion.max = conversionMeans[index] || step.conversion.max;
    });

    return parseStepRanges(steps);
}

export function fillStepRanges(steps: GameStep[], results: GameResults) {
    const [setupStep1, setupStep2, setupStep3, setupStep4] = setup.game.steps;
    const resultWeek5 = sliceResultsByWeek(results.days, 5, 6);

    // Get conversion distributions
    const conversionDistributions = [
        getNormalDistribution(resultWeek5.map(day => transformValue(day.step2 / day.step1, setupStep1.conversion.multiplier, setupStep1.conversion.decimals, true))),
        getNormalDistribution(resultWeek5.map(day => transformValue(day.step3 / day.step2, setupStep2.conversion.multiplier, setupStep2.conversion.decimals, true))),
        getNormalDistribution(resultWeek5.map(day => transformValue(day.step4 / day.step3, setupStep3.conversion.multiplier, setupStep3.conversion.decimals, true))),
        getNormalDistribution(resultWeek5.map(day => transformValue(day.step5 / day.step4, setupStep4.conversion.multiplier, setupStep4.conversion.decimals, true))),
    ];

    // Update step conversions
    steps.forEach((step, index) => {
        const conversionDistribution = conversionDistributions[index];
        if (conversionDistribution) {
            step.conversion.min = conversionDistribution.lower;
            step.conversion.max = conversionDistribution.upper;
        }
    });

    return parseStepRanges(steps);
}

export function parseStepRanges(steps: GameStep[]) {
    const newSteps = deepClone(steps);

    // Loop through continually updated steps
    for (let i = 0; i < newSteps.length; i++) {
        const step = newSteps[i];
        const prevStep = newSteps[i - 1];

        // Get value from previous
        if (prevStep) {
            step.min = Math.round(prevStep.min * prevStep.conversion.min);
            step.max = Math.round(prevStep.max * prevStep.conversion.max);
        }
    }

    return newSteps;
}

export function getHurricaneBands(steps: GameStep[]) {
    const { bands, trials } = setup.game.hurricane;

    // Get step distributions
    const stepDistributions = steps.map(step => {
        const { conversion, max, min, retention } = step;
        return {
            ...getHurricaneStepDistribution(min, max),
            conversion: getHurricaneStepDistribution(conversion.min, conversion.max),
            retention: getHurricaneStepDistribution(retention.min, retention.max),
        };
    });

    // Get trails of weekly revenues
    const trialsWeeklyRevenues = _.range(trials).map(() => {
        // Get daily results
        const days: GameResultDay[] = [];
        _.range(168).map(index => {
            const [d1, d2, d3, d4] = stepDistributions;

            // Get conversions
            const step1Conversion = getNormalInverse(d1.conversion.mean, d1.conversion.standardDeviation);
            const step2Conversion = getNormalInverse(d2.conversion.mean, d2.conversion.standardDeviation);
            const step3Conversion = getNormalInverse(d3.conversion.mean, d3.conversion.standardDeviation);
            const step4Conversion = getNormalInverse(d4.conversion.mean, d4.conversion.standardDeviation);

            // Get retention
            const step4RetentionDay = days[index - setup.game.retention.days - 28];
            const step4Retention = step4RetentionDay ? getNormalInverse(d4.retention.mean, d4.retention.standardDeviation) : 0;
            const step4RetentionValue = step4RetentionDay ? step4Retention * step4RetentionDay.step4 : 0;

            // Get steps
            const step1 = getNormalInverse(d1.mean, d1.standardDeviation);
            const step2 = step1 * step1Conversion;
            const step3 = step2 * step2Conversion + step4RetentionValue;
            const step4 = step3 * step3Conversion;
            const step5 = step4 * step4Conversion;

            // Add day
            const day = {
                step1,
                step2,
                step3,
                step4,
                step4Retention,
                step5,
            };
            days.push(day);
        });

        // Get weekly revenues
        const weeklyResults = getWeeklyResults(days, 0);
        return weeklyResults.map(week => week.step5);
    });

    // Get weekly revenues
    const weeklyRevenues = _.range(24).map(index => {
        return trialsWeeklyRevenues.map(trial => trial[index]);
    });

    // Get band ranges
    const redMin = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, bands.red)));
    const redMax = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, 1 - bands.red)));
    const orangeMin = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, bands.orange)));
    const orangeMax = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, 1 - bands.orange)));
    const yellowMin = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, bands.yellow)));
    const yellowMax = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, 1 - bands.yellow)));
    const greenMin = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, bands.green)));
    const greenMax = weeklyRevenues.map(weekRevenues => _.round(getPercentile(weekRevenues, 1 - bands.green)));

    // Get hurricane bands
    return {
        red: weeklyRevenues.map((_, index) => ([redMin[index], redMax[index]])),
        orange: weeklyRevenues.map((_, index) => ([orangeMin[index], orangeMax[index]])),
        yellow: weeklyRevenues.map((_, index) => ([yellowMin[index], yellowMax[index]])),
        green: weeklyRevenues.map((_, index) => ([greenMin[index], greenMax[index]])),
    } as HurricaneBands;
}

export function getHurricaneStepDistribution(min: number, max: number) {
    return {
        max: max,
        mean: _.mean([min, max]),
        min: min,
        standardDeviation: (max - min) / setup.game.standardDeviation.denominator,
    };
}